import React, { useRef, useState } from "react";
import { C } from "../theme";
import ProfileAvatar from "./ProfileAvatar";
import { uploadProfilePhoto, removeProfilePhoto } from "../services/profilePhoto";

// Tap-to-enlarge profile modal. Shows the angler's photo at a comfortable size
// plus their name and team (when set), and lets them change/remove the photo by
// picking an image from their device. Opened from the small dashboard avatar.
export default function ProfilePhotoModal({ open, onClose, url = "", name = "", teamName = "" }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState("");

  const handlePick = async (e) => {
    const file = e?.target?.files?.[0];
    if (e?.target) e.target.value = ""; // allow re-picking the same file later
    if (!file) return;
    setPhotoError("");
    setUploading(true);
    try {
      await uploadProfilePhoto(file);
    } catch (err) {
      setPhotoError(err?.message || "Couldn't update your photo. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setPhotoError("");
    setUploading(true);
    try {
      await removeProfilePhoto();
    } catch (err) {
      setPhotoError(err?.message || "Couldn't remove your photo. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(4,8,13,0.82)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(86vw, 320px)",
          background: C.settingsBg || C.cardBg,
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          padding: "22px 20px 18px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
          position: "relative",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 10,
            right: 12,
            background: "transparent",
            border: "none",
            color: C.muted,
            fontSize: 16,
            cursor: "pointer",
            lineHeight: 1,
          }}
        >
          ✕
        </button>

        <ProfileAvatar url={url} name={name} size={180} title={name || "Profile photo"} />

        {name && (
          <div style={{ fontSize: 18, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700, textAlign: "center", lineHeight: 1.3 }}>
            {name}
          </div>
        )}

        {teamName ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 10, color: C.teal, letterSpacing: 2, textTransform: "uppercase" }}>Team</span>
            <span style={{ fontSize: 13, color: C.sand || C.white, fontWeight: 600 }}>{teamName}</span>
          </div>
        ) : (
          <div style={{ fontSize: 11, color: C.muted }}>No team set</div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button
            type="button"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={uploading}
            style={{
              padding: "8px 16px",
              borderRadius: 999,
              border: `1px solid ${C.seafoam}66`,
              background: `${C.accent}2E`,
              color: C.white,
              fontSize: 12,
              fontWeight: 700,
              cursor: uploading ? "default" : "pointer",
            }}
          >
            {uploading ? "Saving\u2026" : url ? "Change photo" : "Add photo"}
          </button>
          {url && !uploading && (
            <button
              type="button"
              onClick={handleRemove}
              style={{
                padding: "8px 14px",
                borderRadius: 999,
                border: `1px solid ${C.border}`,
                background: "transparent",
                color: C.muted,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Remove
            </button>
          )}
        </div>
        {photoError && (
          <div style={{ fontSize: 10, color: C.warning, textAlign: "center", lineHeight: 1.4 }}>{photoError}</div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePick} style={{ display: "none" }} />
      </div>
    </div>
  );
}
